import { create } from "zustand";
import { persist } from "zustand/middleware";
import { TenantConfig, UserSettings } from "../types";
import { detectTenantSlugFromHostname } from "../utils/tenantDetect";
import { getPresetById } from "../config/themePresets";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/** Safe fallback so the UI is never blank if the API is unreachable. */
const FALLBACK_TENANT: TenantConfig = {
  id: "brandsmart-default",
  name: "301io",
  slug: detectTenantSlugFromHostname(),
  logo: "/logo.svg",
  appName: "BrandSmart",
  supportEmail: "support@brandsmart.com",
  supportPhone: "1-877-WARRANTY",
  enableAIDiagnosis: true,
  enableWarrantyPlans: true,
  enableBookings: true,
  enableMaintenance: true,
  enableFamilySharing: true,
  enableNetworkDiscovery: true,
  enableOrders: true,
  enableProtectionPlans: true,
  theme: {
    primaryColor: "211 100% 40%",
    secondaryColor: "210 40% 96.1%",
    accentColor: "217.2 91.2% 59.8%",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    borderRadius: "0.5rem",
  },
};

/**
 * Tenant Store — Manages tenant configuration fetched from the backend.
 *
 * On app start, useTenantInit() calls fetchTenant() which hits the public
 * GET /v1/tenants/current endpoint (no auth required). The slug sent in the
 * X-Tenant-Slug header is derived from the browser hostname so the backend
 * resolves the correct tenant automatically.
 *
 * The last-fetched tenant is persisted to localStorage so it is available
 * immediately on the next page load (no flicker). isLoaded/isLoading are
 * intentionally NOT persisted so a fresh fetch runs on every mount.
 */
interface TenantStore {
  tenant: TenantConfig;
  isLoaded: boolean;
  isLoading: boolean;
  fetchError: string | null;
  setTenant: (tenant: TenantConfig) => void;
  fetchTenant: () => Promise<void>;
}

export const useTenantStore = create<TenantStore>()(
  persist(
    (set) => ({
      tenant: FALLBACK_TENANT,
      isLoaded: false,
      isLoading: false,
      fetchError: null,

      setTenant: (tenant) => set({ tenant, isLoaded: true }),

      fetchTenant: async () => {
        const slug = detectTenantSlugFromHostname();
        set({ isLoading: true, fetchError: null });
        try {
          const res = await fetch(`${API_BASE}/v1/tenants/current`, {
            headers: {
              "Content-Type": "application/json",
              "X-Tenant-Slug": slug,
            },
          });

          if (!res.ok) {
            throw new Error(`Server returned ${res.status}`);
          }

          const json = await res.json();
          // Backend wraps in { success, data }
          const data = json?.data ?? json;
          const s = data.settings ?? {};

          const tenantConfig: TenantConfig = {
            id: data.id,
            name: data.name,
            slug: data.slug,
            logo: s.logoUrl || "/logo.svg",
            appName: s.appName || data.name,
            logoUrl: s.logoUrl,
            faviconUrl: s.faviconUrl,
            supportEmail: s.supportEmail || data.contactEmail,
            supportPhone: s.supportPhone || data.contactPhone,
            contactEmail: data.contactEmail,
            contactPhone: data.contactPhone,
            enableAIDiagnosis: s.enableAIDiagnosis ?? true,
            enableWarrantyPlans: s.enableWarrantyPlans ?? true,
            enableBookings: s.enableBookings ?? true,
            enableMaintenance: s.enableMaintenance ?? true,
            enableFamilySharing: s.enableFamilySharing ?? true,
            enableNetworkDiscovery: s.enableNetworkDiscovery ?? true,
            enableOrders: s.enableOrders ?? true,
            enableProtectionPlans: s.enableProtectionPlans ?? true,
            theme: {
              primaryColor: s.primaryColor || "211 100% 40%",
              secondaryColor: s.secondaryColor || "210 40% 96.1%",
              accentColor: s.accentColor || "217.2 91.2% 59.8%",
              fontFamily: s.fontFamily
                ? `'${s.fontFamily}', sans-serif`
                : "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              borderRadius: "0.5rem",
            },
          };

          set({ tenant: tenantConfig, isLoaded: true, isLoading: false });
        } catch (err) {
          // Fall back to whatever is already in the store (persisted from last run)
          // so the app keeps working even if the API is temporarily unreachable.
          set({
            isLoading: false,
            isLoaded: false,
            fetchError: err instanceof Error ? err.message : "Failed to load tenant config",
          });
        }
      },
    }),
    {
      name: "tenant-storage",
      // Only persist the resolved tenant data — never the loading flags.
      partialize: (state) => ({ tenant: state.tenant }),
    }
  )
);

/**
 * Settings Store - Manages user preferences
 */
interface SettingsStore {
  settings: UserSettings;
  updateSettings: (settings: Partial<UserSettings>) => void;
  fontSize: string;
}

const DEFAULT_SETTINGS: UserSettings = {
  fontSize: "medium",
  theme: "light",
  colorTheme: "301io",
  notifications: true,
};

/**
 * Read persisted settings synchronously from localStorage BEFORE Zustand
 * rehydrates. Zustand v5 persist wraps even localStorage reads in a
 * Promise/microtask chain, so the store initialises with the default state on
 * the very first React render — THEN updates asynchronously. This causes
 * useThemeApplier to briefly apply the wrong default theme before the correct
 * one is restored, overwriting the blocking-script CSS vars.
 *
 * Pre-loading the value here ensures the initial state is already correct, so
 * the first useThemeApplier call applies the right colours with zero flash.
 */
function readPersistedSettings(): UserSettings {
  try {
    const raw = localStorage.getItem("settings-storage");
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    const settings = parsed?.state?.settings;
    if (settings && typeof settings === "object") {
      return { ...DEFAULT_SETTINGS, ...settings };
    }
  } catch {
    // fallback to defaults if storage is corrupt / unavailable
  }
  return DEFAULT_SETTINGS;
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set, get) => ({
      settings: readPersistedSettings(),
      updateSettings: (newSettings) =>
        set((state) => ({
          settings: { ...state.settings, ...newSettings },
        })),
      get fontSize() {
        const size = get().settings.fontSize;
        return size === "small" ? "text-sm" : size === "large" ? "text-lg" : "text-base";
      },
    }),
    {
      name: "settings-storage",
      // Apply theme CSS variables immediately when Zustand rehydrates from localStorage.
      // This fires before the first React useEffect, eliminating the flash of default theme.
      onRehydrateStorage: () => (state) => {
        if (!state?.settings?.colorTheme) return;
        try {
          // Get theme preset from centralized configuration
          const preset = getPresetById(state.settings.colorTheme);
          if (!preset) return;
          
          const root = document.documentElement;
          const isDark = root.classList.contains("dark") || state.settings.theme === "dark";
          
          // Apply theme CSS variables
          root.style.setProperty("--primary", preset.primary);
          root.style.setProperty("--brand-primary", preset.primary);
          root.style.setProperty("--brand-primary-hover", preset.primaryHover || preset.primary);
          root.style.setProperty("--ring", preset.primary);
          root.style.setProperty("--accent", preset.accent);
          root.style.setProperty("--brand-accent", preset.accent);
          root.setAttribute("data-color-theme", preset.id);
          
          // Apply foreground color if specified and not in dark mode
          if (preset.foreground && !isDark) {
            root.style.setProperty("--foreground", preset.foreground);
            root.style.setProperty("--card-foreground", preset.foreground);
            root.style.setProperty("--popover-foreground", preset.foreground);
          }
          
          // Apply custom font family if specified
          if (preset.fontFamily) {
            root.style.setProperty("--theme-font-family", `'${preset.fontFamily}', sans-serif`);
          }
          
          // Apply custom button radius if specified
          if (preset.buttonRadius) {
            root.style.setProperty("--theme-button-radius", preset.buttonRadius);
          }
        } catch (_) { /* fail silently */ }
      },
    }
  )
);

